import { lzFPluralize } from '@lz/services/String';
import { LzTMediaContentType, LzTMediaFileType } from '../commons/Media';
import LzMOption from '../commons/Option';

export type LzTCrudFieldGroup = {
  [key: string]: LzNCrudField;
};

export type LzTCrudFieldType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'reference'
  | 'media';

export interface LzNCrudField {
  size?: number;
  type?: LzTCrudFieldType;
  translatable?: boolean;
  required?: boolean;
  default?: any;
  options?: LzMOption[];
  refTableName?: string;
  refTableIdField?: string;
  refTableTitleField?: string;
  refAppendedDisplayField?: string;
  refFilterFieldKeys?: string[];
  mediaCategory?: string;
  mediaFileType?: LzTMediaFileType;
  mediaContentType?: LzTMediaContentType;
  mediaIsPrivate?: boolean;
  mediaUseBrowser?: boolean;
}

class LzMCrudField implements LzNCrudField {
  size: number;
  type: LzTCrudFieldType;
  translatable: boolean;
  required: boolean;
  default: any;
  options: LzMOption[] | undefined;
  refTableName?: string;
  refTableIdField?: string;
  refTableTitleField?: string;
  refAppendedDisplayField?: string;
  refFilterFieldKeys?: string[];
  mediaCategory?: string;
  mediaFileType?: LzTMediaFileType;
  mediaContentType?: LzTMediaContentType;
  mediaIsPrivate?: boolean;
  mediaUseBrowser?: boolean;

  static DEFAULT_VALUES_FOR_TYPES = {
    string: '',
    number: 0,
    boolean: false,
    reference: null,
    media: null,
  };

  constructor(model: LzNCrudField, key: string) {
    this.size = model.size ? +model.size : 50;
    this.type = model.type ? model.type : 'string';
    this.translatable = !!model.translatable;
    this.required =
      typeof model.required === 'boolean' ? model.required : false;
    this.default =
      typeof model.default !== 'undefined'
        ? model.default
        : LzMCrudField.DEFAULT_VALUES_FOR_TYPES[this.type];
    this.options = model.options;

    // If field is a reference to another table then load reference related fields
    if (this.type === 'reference') {
      this.refTableName = model.refTableName
        ? model.refTableName
        : LzMCrudField.getTableNameFromKey(key);
      this.refTableIdField = model.refTableIdField
        ? model.refTableIdField
        : 'id';
      this.refTableTitleField = model.refTableTitleField
        ? model.refTableTitleField
        : 'name';
      this.refAppendedDisplayField = model.refAppendedDisplayField
        ? model.refAppendedDisplayField
        : LzMCrudField.getAppendedDisplayField(key, this.refTableTitleField);
      this.refFilterFieldKeys = model.refFilterFieldKeys
        ? model.refFilterFieldKeys
        : [];
    }

    // If field is media then load media related fields
    if (this.type === 'media') {
      this.mediaCategory = model.mediaCategory || ''; // specified to shortlist the uploadable images and set in the resulting media object
      this.mediaFileType = model.mediaFileType || 'image'; // specified to shortlist the uploadable images and set in the resulting media object
      this.mediaContentType = model.mediaContentType || 'image/*'; // specified to shortlist the uploadable images, the uploaded one will be set in the resulting media object
      this.mediaIsPrivate = !!model.mediaIsPrivate; // specified to shortlist the uploadable images and set in the resulting media object
      this.mediaUseBrowser = !!model.mediaUseBrowser; // if set, then media browser is used, else normal uploading is used
    }
  }

  static parseData(data: any): LzTCrudFieldGroup {
    const out: LzTCrudFieldGroup = {};

    if (!data) {
      return out;
    }

    const keys = Object.keys(data);
    for (const key of keys) {
      out[key] = new LzMCrudField(data[key], key);
    }

    return out;
  }

  static getTableNameFromKey(key: string): string {
    if (!key) {
      return '';
    }

    return lzFPluralize(key.substring(0, key.length - 3));
  }

  static getAppendedDisplayField(
    key: string,
    otherTableTitleField: string
  ): string {
    return key.substring(0, key.length - 2) + otherTableTitleField;
  }
}

export default LzMCrudField;
